import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChipIcon } from './ChipIcon.js';
import { api } from '../lib/api.js';
import { useToast } from '../stores/toast.js';
import { useAuth } from '../stores/auth.js';
import { haptic } from '../lib/telegram.js';

interface ReferralSheetProps {
  open: boolean;
  onClose: () => void;
}

const BOT_USERNAME = 'durakapp_game_bot';
const MINI_APP_PATH = 'play';
const BONUS_PER_SIDE = 50;

export function ReferralSheet({ open, onClose }: ReferralSheetProps) {
  const me = useAuth((s) => s.user);
  const pushToast = useToast((s) => s.push);
  const [stats, setStats] = useState<{ invitedCount: number; bonusedCount: number } | null>(null);

  const refLink = me ? `https://t.me/${BOT_USERNAME}/${MINI_APP_PATH}?startapp=ref_${me.id}` : '';
  const shareText = 'Сыграем в подкидного на фишках? Дам тебе 50 фишек на старте.';
  const shareUrl =
    'https://t.me/share/url?url=' +
    encodeURIComponent(refLink) +
    '&text=' +
    encodeURIComponent(shareText);

  useEffect(() => {
    if (!open) return;
    api.referralStats()
      .then((s) => setStats({ invitedCount: s.invitedCount, bonusedCount: s.bonusedCount }))
      .catch(() => {/* keep silent — stats are decorative */});
  }, [open]);

  const copyLink = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(refLink);
      haptic('light');
      pushToast('Ссылка скопирована', 'info');
    } catch {
      pushToast('Не удалось скопировать', 'error');
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={onClose}
            className="fixed inset-0 z-40"
            style={{ background: 'rgba(0, 0, 0, 0.55)' }}
          />
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', stiffness: 320, damping: 32 }}
            className="fixed inset-x-0 bottom-0 z-50 mx-auto w-full max-w-[480px] flex flex-col gap-4 px-5 pb-6 pt-3"
            style={{
              background:
                'linear-gradient(180deg, color-mix(in oklab, var(--color-felt-deep) 95%, white 4%) 0%, var(--color-felt-deep) 100%)',
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              borderTop: '1px solid color-mix(in oklab, var(--color-gold) 35%, transparent)',
              boxShadow: '0 -20px 40px -20px rgba(0,0,0,0.7)',
              paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
            }}
          >
            <div className="mx-auto h-1 w-10 rounded-full bg-ivory/25" />

            <div className="flex flex-col gap-1">
              <h2 className="heading-display text-2xl">Пригласи друга</h2>
              <p className="text-xs text-ivory/55 flex items-center gap-1 flex-wrap">
                За первую сыгранную партию друга — по {BONUS_PER_SIDE} <ChipIcon size={11} /> вам
                обоим. Без ограничений на количество приглашений.
              </p>
            </div>

            {stats && (
              <div
                className="flex items-center justify-between rounded-2xl px-4 py-3"
                style={{
                  background: 'var(--color-glass)',
                  border: '1px solid color-mix(in oklab, var(--color-gold) 30%, transparent)',
                }}
              >
                <div className="flex flex-col">
                  <span className="text-[10px] uppercase tracking-wider text-ivory/55">Приглашено</span>
                  <span style={{ fontFamily: 'var(--font-display)', fontSize: 22 }}>{stats.invitedCount}</span>
                </div>
                <div className="flex flex-col items-end">
                  <span className="text-[10px] uppercase tracking-wider text-ivory/55">Получено бонусов</span>
                  <span
                    className="flex items-center gap-1 text-gold"
                    style={{ fontFamily: 'var(--font-display)', fontSize: 22 }}
                  >
                    {stats.bonusedCount * BONUS_PER_SIDE} <ChipIcon size={16} />
                  </span>
                </div>
              </div>
            )}

            <div className="flex flex-col gap-2">
              <label className="text-[10px] uppercase tracking-wider text-ivory/55">Твоя ссылка</label>
              <button
                onClick={() => void copyLink()}
                className="flex items-center justify-between gap-2 rounded-2xl px-3 py-2.5 text-left text-sm"
                style={{
                  background: 'var(--color-glass)',
                  border: '1px solid color-mix(in oklab, var(--color-gold) 30%, transparent)',
                  fontFamily: 'var(--font-display)',
                  color: 'var(--color-ivory)',
                }}
              >
                <span className="truncate">{refLink}</span>
                <span className="shrink-0 text-xs text-gold">копировать</span>
              </button>
            </div>

            <a
              href={shareUrl}
              target="_blank"
              rel="noreferrer"
              onClick={() => haptic('medium')}
              className="btn-primary text-center"
            >
              Поделиться в Telegram
            </a>

            <p className="text-[11px] text-ivory/45">
              Бонус приходит автоматически после того, как друг откроет приложение по твоей
              ссылке и сыграет первую партию (любой стол со ставкой или Free).
            </p>

            <button
              onClick={onClose}
              className="text-center text-sm text-ivory/55 underline-offset-2 hover:underline"
            >
              Закрыть
            </button>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
